// src/pages/Agents/MemoryProfiles.tsx — supermemory / context profiles (Phase 4e).
import React, { useCallback, useEffect, useState } from 'react';
import { Brain, Plus, RefreshCw, Trash2, AlertCircle, Save } from 'lucide-react';
import { memoryProfilesService, MemoryProfile } from 'api/services/memoryProfiles.service';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';

const PROVIDERS: MemoryProfile['provider'][] = ['NONE', 'SUPERMEMORY'];
const SCOPES: MemoryProfile['scope'][] = ['BUSINESS', 'AGENT', 'CALLER'];
const blank = (): MemoryProfile => ({ name: '', provider: 'SUPERMEMORY', scope: 'BUSINESS', enabled: true });

const MemoryProfiles: React.FC = () => {
  const [rows, setRows] = useState<MemoryProfile[]>([]);
  const [draft, setDraft] = useState<MemoryProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const res = await memoryProfilesService.list();
    if (res.success) setRows(res.data || []); else setError(res.error || 'Failed to load');
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!draft) return;
    if (!draft.name.trim()) { setError('name is required'); return; }
    const res = draft.id ? await memoryProfilesService.update(draft.id, draft) : await memoryProfilesService.create(draft);
    if (res.success) { setDraft(null); load(); } else setError(res.error || 'Save failed');
  };
  const remove = async (m: MemoryProfile) => {
    if (!m.id) return;
    const res = await memoryProfilesService.remove(m.id);
    if (res.success) setRows(list => list.filter(x => x.id !== m.id)); else setError(res.error || 'Delete failed');
  };

  const inputCls = 'px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm';

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2"><Brain className="w-6 h-6 text-purple-600" /> Memory Profiles</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Supermemory / long-term context an agent references — shared profiles give a customer unified memory across channels.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 inline-flex items-center gap-2"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh</button>
          <button onClick={() => setDraft(blank())} className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 inline-flex items-center gap-2"><Plus className="w-4 h-4" /> New Profile</button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2 flex items-center gap-2"><AlertCircle className="w-4 h-4" />{error}</div>}

      {draft && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 grid grid-cols-1 md:grid-cols-2 gap-3 border border-purple-200">
          <label className="text-xs text-gray-500 md:col-span-2">Name<input className={inputCls + ' w-full'} value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} /></label>
          <label className="text-xs text-gray-500">Provider<select className={inputCls + ' w-full'} value={draft.provider} onChange={e => setDraft({ ...draft, provider: e.target.value as any })}>{PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}</select></label>
          <label className="text-xs text-gray-500">Scope<select className={inputCls + ' w-full'} value={draft.scope} onChange={e => setDraft({ ...draft, scope: e.target.value as any })}>{SCOPES.map(s => <option key={s} value={s}>{s}</option>)}</select></label>
          <label className="text-xs text-gray-500">Namespace<input className={inputCls + ' w-full'} value={draft.namespace || ''} onChange={e => setDraft({ ...draft, namespace: e.target.value })} placeholder="defaults to business id" /></label>
          <label className="text-xs text-gray-500">Retention (days)<input type="number" className={inputCls + ' w-full'} value={draft.retention_days ?? ''} onChange={e => setDraft({ ...draft, retention_days: parseInt(e.target.value) })} /></label>
          <label className="text-xs text-gray-500 md:col-span-2">API key reference (secret name)<input className={inputCls + ' w-full'} value={draft.api_key_ref || ''} onChange={e => setDraft({ ...draft, api_key_ref: e.target.value })} placeholder="never the raw key" /></label>
          <div className="md:col-span-2 flex gap-2">
            <button onClick={save} className="px-3 py-2 bg-purple-600 text-white rounded-lg inline-flex items-center gap-2"><Save className="w-4 h-4" /> Save</button>
            <button onClick={() => setDraft(null)} className="px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg">Cancel</button>
          </div>
        </div>
      )}

      {loading && rows.length === 0 ? <LoadingSpinner /> : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700/50"><tr>{['Name', 'Provider', 'Scope', 'Namespace', ''].map(h => <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-gray-500">No memory profiles. Click <b>New Profile</b>.</td></tr>}
                {rows.map(m => (
                  <tr key={m.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3 text-sm font-medium text-purple-600 cursor-pointer hover:underline" onClick={() => setDraft(m)}>{m.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{m.provider}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{m.scope}</td>
                    <td className="px-4 py-3 text-xs font-mono text-gray-500">{m.namespace || '—'}</td>
                    <td className="px-4 py-3 text-right"><button onClick={() => remove(m)} className="p-1.5 text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemoryProfiles;
